import { Component, ComponentFactoryResolver, ComponentRef, Input, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GameRecord, HuntingDistrict, HuntingEvent, IshowSubFormGR, NetworkConstants, Person, PointOfInterest } from 'diwimamodelweb/lib';
import { deepCopy, diffObject } from 'diwimamodelweb/lib/utils/obejct-diff';
import { GamerecordService } from '../gamerecord.service';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { GenericListItemComponent } from '../generic-list-item/generic-list-item.component';
import { InjectComponentDirective } from '../inject-component.directive';
import { GameRecordForm, GameRecordSubForms } from '../model/form';
import { ListItem, SelectableListItem } from '../model/list';
import { UserFeedbackService } from '../user-feedback.service';
import { SettingsService } from '../settings.service';
import { ContextAccess, changedGeoFields, coordKey, isGeoLocked, isReadOnly, resolveContextAccess } from '../context-rights-picker/context-access';

const GAME_RECORD_GEO_FIELDS = ['placeOfKilling', 'hunterLocation', 'huntingDistrictId']

function gameRecordGeoState(record: any): Record<string, any> {
  return {
    placeOfKilling:    coordKey(record?.placeOfKilling?.location),
    hunterLocation:    coordKey(record?.hunterLocation?.location),
    huntingDistrictId: record?.huntingDistrictId ?? null,
  }
}

@Component({
  selector: 'app-gamerecord-form-handler',
  templateUrl: './gamerecord-form-handler.component.html',
  styleUrls: ['./gamerecord-form-handler.component.css']
})
export class GamerecordFormHandlerComponent implements OnInit {

  @Input() data: GameRecordForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() objectService: GamerecordService
  @Input() componentFactoryResolver: ComponentFactoryResolver
  @Input() translateService: TranslateService

  @ViewChild(InjectComponentDirective, {static: true}) view: InjectComponentDirective;

  showBack = true
  currentlyActiveForm = GameRecordSubForms.BASE
  componentRef: ComponentRef<any>|undefined = undefined
  viewContainerRef: ViewContainerRef|undefined = undefined

  allPerson: Person[]|undefined = undefined
  allHuntingDistricts: HuntingDistrict[]|undefined = undefined
  allEvents: HuntingEvent[]|undefined = undefined
  allPoi: PointOfInterest[]|undefined = undefined

  oldGameRecord: GameRecord
  contextAccess: ContextAccess = 'owner'

  constructor(
    private gameRecordService : GamerecordService,
    private settingsService: SettingsService,
  ) {}

  async ngOnInit(): Promise<void> {
    // list select data... 
    if(this.data.data.identifier){
      const imgLinks = await this.gameRecordService.loadGameRecordImg(this.data.data.identifier)

      if(imgLinks && imgLinks.length > 0) {
        this.data.data.pictureURLlinks = imgLinks
      }
    }
    this.allPerson = this.data.person
    this.allHuntingDistricts = this.data.huntingDistricts
    this.allEvents = this.data.events
    this.allPoi = this.data.geoObjects

    const context = this.settingsService.selectedUserContext$?.value
    const subIds = this.settingsService.getSubcontextIds();//?? (context as any)?.subContextIds
    this.contextAccess = resolveContextAccess(
      this.data.data as any,
      context?.identifier,
      subIds,
    )

    this.data.onSubmit = async () =>{
      if (this.rejectForbiddenChange()) return

      try {
        this.userFeedbackService.loading.emit(true)
        const newGr = deepCopy(this.data.data, this.data.data.replacerFn)
        await this.objectService.updateGameRecord(this.oldGameRecord, newGr)
        const sbSuccessSave = await this.translateService.instant('changes_saved')
        this.userFeedbackService.loading.emit(false)
        this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
        this.data.onCancle()
      } catch (error) {
        console.error(error)
        this.userFeedbackService.loading.emit(false)
        this.userFeedbackService.showDialog.emit(
          {
            entryComponent: GenericDialogComponent,
            title: "Error",
            message: error?.error?.message ? error?.error?.message : JSON.stringify(error),
          }
        )
      }
    }
    this.oldGameRecord = deepCopy(this.data.data, this.data.data.replacerFn)
    this.injectComponent(this.data.subFormRefs.get(GameRecordSubForms.BASE))

  }

  private rejectForbiddenChange(): boolean {
    if (isReadOnly(this.contextAccess)) {
      this.userFeedbackService.showSnackBar.emit('Dieser Datensatz ist nur zur Ansicht freigegeben.')
      return true
    }
    if (isGeoLocked(this.contextAccess)) {
      const changed = changedGeoFields(
        gameRecordGeoState(this.oldGameRecord),
        gameRecordGeoState(this.data.data),
        GAME_RECORD_GEO_FIELDS,
      )
      if (changed.length) {
        this.userFeedbackService.showSnackBar.emit('Geodaten können nicht geändert werden.')
        return true
      }
    }
    return false
  }

  async navBack() {
    if (this.currentlyActiveForm == GameRecordSubForms.BASE) {
      // check if data changed?
      const newGr = deepCopy(this.data.data, this.data.data.replacerFn)
      const diff = diffObject(this.oldGameRecord, newGr, {})
      const context = this.settingsService.selectedUserContext$?.value
      const rights = this.settingsService.rightsPercontext.get(context.identifier)

      if (Object.keys(diff).length == 0) this.data.onCancle()
      else {
        // display dialog save changes?
        const title = this.translateService.instant('title_gr_changes')
        const message = this.translateService.instant('msg_gr_changes')
        const grRights = rights[NetworkConstants.COLLECTION_GAMERECORD]

        this.userFeedbackService.showDialog.emit({
          entryComponent: GenericDialogComponent,
          title,
          message,
          cancleCallback: async () => {
            const sbChangesDismissed = this.translateService.instant('changes_dismissed')
            this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
            this.data.onCancle()
          },
          positiveCallback: ()=> {
            if( grRights['U'])
            this.data.onSubmit()
            else {
              const message = this.translateService.instant('not_allowed_update')
              this.userFeedbackService.showSnackBar.emit(message) 
            }
          }
        })
      }
    } else {
      this.currentlyActiveForm = GameRecordSubForms.BASE
      this.injectComponent(this.data.subFormRefs.get(GameRecordSubForms.BASE))
    }
  }

  injectComponent(componentRef: Type<any>, data?:any) {
    // this.currentlyActiveForm
    this.viewContainerRef?.clear()
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentRef)
    this.viewContainerRef = this.view.viewContainerRef
    this.componentRef = this.viewContainerRef.createComponent(componentFactory)
    if (this.currentlyActiveForm == GameRecordSubForms.BASE) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.formBuilder = this.formBuilder
      this.componentRef.instance.userFeedbackService = this.userFeedbackService
      this.componentRef.instance.translateService = this.translateService
      this.componentRef.instance.showSubForm.subscribe((values: IshowSubFormGR) => {
        const { subForm, data } = values
        const form = subForm as GameRecordSubForms
        const subForm_ = this.data.subFormRefs.get(form)
        if (!subForm_) return
        this.currentlyActiveForm = form
        this.injectComponent(subForm_, data) 
      })
    } else if (this.currentlyActiveForm == GameRecordSubForms.SPECIES) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.formBuilder = this.formBuilder
      // this.componentRef.instance.userFeedbackService = this.userFeedbackService
      this.componentRef.instance.translateService = this.translateService
      this.componentRef.instance.submit.subscribe(() => {
        const form = this.data.subFormRefs.get(GameRecordSubForms.BASE)
        this.currentlyActiveForm = GameRecordSubForms.BASE
        this.injectComponent(form)
      })
    } else if (this.currentlyActiveForm == GameRecordSubForms.LABSAMPLES) {
      this.componentRef.instance.data = data
      this.componentRef.instance.formBuilder = this.formBuilder
      // this.componentRef.instance.userFeedbackService = this.userFeedbackService
      this.componentRef.instance.translateService = this.translateService
      
    } else if (this.currentlyActiveForm == GameRecordSubForms.ERLEGER) {
      let listOfSelectableListItems: SelectableListItem[] = []
      this.data.person.forEach((person) => {
        const selListItem = new SelectableListItem(
          person.anzeigeName,
          "",
          person,
          GenericListItemComponent
        )
        selListItem.addListener(this)
        listOfSelectableListItems.push(
          selListItem
        )
      })
      this.componentRef.instance.data = listOfSelectableListItems
    } else if (this.currentlyActiveForm == GameRecordSubForms.DISTRICTS) {
      let listOfSelectableListItems: SelectableListItem[] = []
      this.data.huntingDistricts.forEach((district) => {
        const selListItem = new SelectableListItem(
          district.name,
          "",
          district,
          GenericListItemComponent
        )
        selListItem.addListener(this)
        listOfSelectableListItems.push(
          selListItem
        )
      })
      this.componentRef.instance.data = listOfSelectableListItems
    } else if (this.currentlyActiveForm == GameRecordSubForms.EVENTS) {
      let listOfSelectableListItems: SelectableListItem[] = []
      this.data.events.forEach((event) => {
        const selListItem = new SelectableListItem(
          event.name,
          "",
          event,
          GenericListItemComponent
        )
        selListItem.addListener(this)
        listOfSelectableListItems.push(
          selListItem
        )
      })
      this.componentRef.instance.data = listOfSelectableListItems
    } else if (this.currentlyActiveForm == GameRecordSubForms.UTILIZATION_OPTION) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.formBuilder = this.formBuilder
      this.componentRef.instance.translateService = this.translateService
      this.componentRef.instance.submit.subscribe(() => {
        const form = this.data.subFormRefs.get(GameRecordSubForms.BASE)
        this.currentlyActiveForm = GameRecordSubForms.BASE
        this.injectComponent(form)
      })
    } else if (
      this.currentlyActiveForm == GameRecordSubForms.PLACEOFKILLING
    ) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.fieldName = GameRecordSubForms.PLACEOFKILLING
      this.componentRef.instance.submit.subscribe(() => {
        const form = this.data.subFormRefs.get(GameRecordSubForms.BASE)
        this.currentlyActiveForm = GameRecordSubForms.BASE
        this.injectComponent(form)
      })
    } else if (
      this.currentlyActiveForm == GameRecordSubForms.HUNTERLOCATION
    ) {
      this.componentRef.instance.data = this.data
      this.componentRef.instance.fieldName = GameRecordSubForms.HUNTERLOCATION
      this.componentRef.instance.submit.subscribe(() => {
        const form = this.data.subFormRefs.get(GameRecordSubForms.BASE)
        this.currentlyActiveForm = GameRecordSubForms.BASE
        this.injectComponent(form)
      })
    }
    
  }

  onClick(listItem: ListItem) {
    if (listItem.data.className == Person.className) {
      const {identifier, anzeigeName} = listItem.data
      this.data.data.setFromAny({
        erlegerId: identifier,
        erlegerAnzeigeName: anzeigeName
      })
      this.currentlyActiveForm = GameRecordSubForms.BASE
      this.injectComponent(this.data.subFormRefs.get(GameRecordSubForms.BASE))
    } else if (listItem.data.className == HuntingDistrict.className) {
      const {identifier, name, administrativeName} = listItem.data as HuntingDistrict
      this.data.data.setFromAny(
        {
          huntingDistrictId: identifier,
          huntingDistrictName: name,
          huntingDistrictAdministrativeName: administrativeName
        })
      this.currentlyActiveForm = GameRecordSubForms.BASE
      this.injectComponent(this.data.subFormRefs.get(GameRecordSubForms.BASE))
    } else if (listItem.data.className == HuntingEvent.className) {
      const {identifier, name} = listItem.data as HuntingEvent
      this.data.data.setFromAny(
        {
          eventId: identifier,
          eventName: name
        })
      this.currentlyActiveForm = GameRecordSubForms.BASE
      this.injectComponent(this.data.subFormRefs.get(GameRecordSubForms.BASE))
    }
  }

}
